import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { debounceTime } from 'rxjs/operators';

// Services
import { AuthService } from '../../../services/auth.service';
import { 
  CallCenterBusinessService,
  CallCenterStateService,
  CallCenterDialogService, 
  CallCenterAssignmentService,
  CallCenterChipManagementService
} from './services';

// Components
import { CallCenterChipComponent } from '../../../shared/components/call-center-chip/call-center-chip.component';

// Interfaces
import { 
  CallCenterDatabaseTableData, 
  FilterData, 
  StatusOption
} from '../../../interfaces/call-center-database.interface';
import type { CallCenterChipConfig } from '../../../shared/components';

// Utils
import { CallCenterTableUtils } from './utils';

@Component({
  selector: 'app-call-center-database-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    CallCenterChipComponent
  ],
  templateUrl: './call-center-database-table.component.html',
  styleUrl: './call-center-database-table.component.scss'
})
export class CallCenterDatabaseTable implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() title: string = 'Adatbázisom';
  @Input() data: CallCenterDatabaseTableData[] = [];
  @Input() filterData: any = null; // Filter data from wrapper component (legacy, nem használt szerver oldali lapozásnál)
  @Input() hidePaginator: boolean = false;
  @Input() totalRecords: number = 0; // Szerver oldali összes rekord száma
  @Input() serverSidePagination: boolean = false; // Szerver oldali lapozás bekapcsolása
  @Input() currentPage: number = 0; // Szerver oldali aktuális oldal index (wrapper-ből szinkronizálva)
  @Input() set callCenterStaffInput(users: any[]) {
    // Wrapper-ből kapott call center munkatársak átvétele — csak aktív felhasználók a hozzárendelési menühöz
    if (users && users.length > 0) {
      this.callCenterUsers = users.filter((u: any) => u.is_active !== false);
    }
  }
  @Output() dataChanged = new EventEmitter<void>();
  @Output() pageChanged = new EventEmitter<{ pageIndex: number; pageSize: number }>();
  @Output() sortChanged = new EventEmitter<{ active: string; direction: string }>();

  displayedColumns: string[] = [];

  // Dialog loading state
  isLoadingDialog = false;
  private isDialogOpen = false;

  // Call Center staff properties
  callCenterUsers: any[] = [];
  selectedAssignee: any = null;
  isAssigning = false;

  // Paginator settings
  private readonly PAGE_NAME = 'call_center_database';
  private readonly PAGINATOR_STORAGE_KEY = 'call-center-paginator-settings';

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('tableTop', { read: ElementRef }) tableTop!: ElementRef;

  // Inicializálás flag — amíg true, a paginator/sort események NEM triggerelnek API hívást
  private initializing = true;

  constructor(
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private businessService: CallCenterBusinessService,
    private stateService: CallCenterStateService,
    private callCenterDialogService: CallCenterDialogService,
    private callCenterAssignmentService: CallCenterAssignmentService,
    private chipManagementService: CallCenterChipManagementService
  ) {}

  ngOnInit() {
    // Initialize displayed columns based on user role
    this.initializeColumns();
    
    this.initializeData();
    
    // Call Center felhasználókat a wrapper komponens tölti be (callCenterStaff),
    // NEM kell itt is betölteni — a dupla call-center-users API hívás elkerülése érdekében
  }

  private initializeColumns() {
    if (this.authService.isCallCenterEmployee() || this.authService.hasRole('deputy-CEO')) {
      // Call center munkatársak és helyettes vezérigazgató esetén nincs checkbox és assignment funkció
      this.displayedColumns = ['date', 'notes', 'status', 'companyInfo', 'officials', 'phoneNumbers'];
    } else {
      // Admin és manager esetén minden oszlop látható (kivéve actions)
      this.displayedColumns = ['select', 'date', 'notes', 'status', 'companyInfo', 'officials', 'phoneNumbers'];
    }
  }

  // Getter method for template
  get isCallCenterEmployee(): boolean {
    return this.authService.isCallCenterEmployee() || this.authService.hasRole('deputy-CEO');
  }

  // Getter for paginated data - works for both desktop and mobile views
  get paginatedData(): CallCenterDatabaseTableData[] {
    if (this.serverSidePagination) {
      // Szerver oldali lapozásnál a data már az aktuális oldal adatait tartalmazza
      return this.data;
    }
    return this.stateService.getPaginatedData(this.paginator, this.hidePaginator);
  }

  // Getter methods delegating to services
  get dataSource() {
    return this.stateService.dataSource;
  }

  get selection() {
    return this.stateService.selection;
  }

  get statusOptions() {
    return this.stateService.statusOptions;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && this.data) {
      // Szerver oldali lapozásnál a DataSource mindig az aktuális oldal adatait tartalmazza
      this.initializeData();

      // Új adatok érkezésekor (szűrés, lapozás, rendezés) kijelölések törlése
      this.stateService.clearSelection();

      this.cdr.detectChanges();
    }

    // Paginator pageIndex szinkronizálása a wrapper currentPage-ével
    // Szűréskor a wrapper 0-ra állítja a currentPage-et, amit itt tükrözünk a paginator-on
    if (changes['currentPage'] && this.paginator && this.serverSidePagination) {
      this.paginator.pageIndex = this.currentPage;
    }
    
    if (changes['filterData'] && this.filterData && !this.serverSidePagination) {
      // Kliens oldali szűrés csak ha nincs szerver oldali lapozás
      this.onFiltersChanged(this.filterData);
    }
  }

  private initializeData() {
    if (this.serverSidePagination) {
      // Szerver oldali lapozásnál nincs filterPredicate — az adatok már szűrve érkeznek
      this.stateService.initializeDataSourceServerSide(this.data);
    } else {
      this.stateService.initializeDataSource(this.data);
    }
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  ngAfterViewInit() {
    if (this.paginator && !this.hidePaginator) {
      // 1. LocalStorage-ból azonnal betöltés (gyors UX)
      this.loadPaginatorSettingsFromLocalStorage();

      // 2. Backend-ről betöltés (szinkronizált adat)
      this.loadPaginatorSettingsFromBackend();

      // 3. Változások kezelése: szerver oldali lapozásnál emit, kliens oldali lapozásnál lokális
      this.paginator.page
        .pipe(debounceTime(100))
        .subscribe(() => {
          // Inicializálás közben NEM triggerelünk API hívást (végtelen ciklus elkerülése)
          if (this.initializing) return;

          this.stateService.updatePaginatorState(this.paginator.pageIndex);
          this.savePaginatorSettings();
          
          if (this.serverSidePagination) {
            // Szerver oldali lapozás: értesítjük a wrapper-t, ami új API hívást indít
            this.pageChanged.emit({
              pageIndex: this.paginator.pageIndex,
              pageSize: this.paginator.pageSize
            });
          }

          // Lapozás után vissza a táblázat tetejére
          this.scrollToTop();
          this.cdr.detectChanges();
        });

      if (!this.serverSidePagination) {
        // Kliens oldali lapozásnál a DataSource maga lapolja az adatokat
        this.dataSource.paginator = this.paginator;
      }
    }
    
    if (this.sort) {
      if (this.serverSidePagination) {
        // Szerver oldali rendezés: sort változásra emit
        this.sort.sortChange.subscribe(() => {
          // Inicializálás közben NEM triggerelünk API hívást
          if (this.initializing) return;

          // Rendezésnél az első oldalra ugrunk
          if (this.paginator) {
            this.paginator.pageIndex = 0;
          }
          this.sortChanged.emit({
            active: this.sort.active,
            direction: this.sort.direction
          });
        });
      } else {
        this.dataSource.sort = this.sort;
      }
    }

    // Inicializálás vége — ezután a paginator/sort események már triggerelhetnek API hívást
    // setTimeout szükséges, mert a paginator settings betöltés aszinkron
    setTimeout(() => {
      this.initializing = false;
    }, 500);
  }

  /**
   * Táblázat tetejére görgetés lapozás után
   */
  private scrollToTop(): void {
    if (this.tableTop?.nativeElement) {
      this.tableTop.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Paginator beállítások mentése LocalStorage-ba és Backend-re
   */
  private savePaginatorSettings(): void {
    if (!this.paginator) return;

    const settings = {
      pageIndex: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize
    };

    // LocalStorage mentés (azonnal)
    try {
      localStorage.setItem(this.PAGINATOR_STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('LocalStorage mentés sikertelen:', error);
    }

    // Backend mentés (háttérben)
    this.authService.savePaginatorSettings(this.PAGE_NAME, settings).subscribe({
      error: (error) => console.error('Backend mentés sikertelen:', error)
    });
  }

  /**
   * Paginator beállítások betöltése LocalStorage-ból
   */
  private loadPaginatorSettingsFromLocalStorage(): void {
    const savedSettings = localStorage.getItem(this.PAGINATOR_STORAGE_KEY);
    if (savedSettings && this.paginator) {
      try {
        const settings = JSON.parse(savedSettings);
        this.paginator.pageIndex = settings.pageIndex || 0;
        this.paginator.pageSize = settings.pageSize || 10;
      } catch (error) {
        console.error('LocalStorage betöltés sikertelen:', error);
      }
    }
  }

  /**
   * Paginator beállítások betöltése Backend-ről
   */
  private loadPaginatorSettingsFromBackend(): void {
    this.authService.getPaginatorSettings(this.PAGE_NAME).subscribe({
      next: (settings) => {
        if (this.paginator && settings) {
          const prevPageSize = this.paginator.pageSize;
          this.paginator.pageIndex = settings.pageIndex || 0;
          this.paginator.pageSize = settings.pageSize || 10;
          // LocalStorage szinkronizálás
          localStorage.setItem(this.PAGINATOR_STORAGE_KEY, JSON.stringify(settings));

          // Csak pageSize változásnál értesítjük a wrapper-t (újratöltés szükséges)
          // pageIndex változás NEM triggerel újratöltést az inicializálás során,
          // mert a wrapper mindig page 0-ról indul
          if (this.serverSidePagination && prevPageSize !== this.paginator.pageSize) {
            this.pageChanged.emit({
              pageIndex: this.paginator.pageIndex,
              pageSize: this.paginator.pageSize
            });
          }
        }
      },
      error: (error) => console.error('Backend betöltés sikertelen:', error)
    });
  }

  // Filter method
  private triggerFilterUpdate() {
    this.stateService.triggerFilterUpdate();
  }

  loadCallCenterUsers() {
    this.authService.getCallCenterUsers().subscribe({
      next: (users) => {
        // Csak aktív felhasználók jelenjenek meg a hozzárendelési menüben
        this.callCenterUsers = users.filter((u: any) => u.is_active !== false);
        
        // Frissítjük a filter predicate-et az új felhasználókkal
        this.applyAllFilters();
      },
      error: (error) => {
        this.businessService.showError(`Hiba történt a Call Center felhasználók betöltése során: ${error.message || error}`);
        this.callCenterUsers = [];
      }
    });
  }

  onAssigneeSelected(assignee: any) {
    this.selectedAssignee = assignee;
    
    // Ha van kiválasztott munkatárs és vannak kijelölt sorok
    if (assignee && this.selection.hasValue()) {
      this.assignSelectedCompanies(assignee);
    }
  }

  // Új metódus a kijelölt cégek hozzárendeléséhez
  assignSelectedCompanies(assignee: any) {
    if (this.isAssigning) {
      return;
    }
    this.isAssigning = true;

    const companyIds = this.selection.selected.map((row: CallCenterDatabaseTableData) => row.id);
    
    this.callCenterAssignmentService.assignCompanies(assignee, companyIds).subscribe({
      next: (response) => {
        this.isAssigning = false;
        if (response.success) {
          // Töröljük a kijelölést
          this.stateService.clearSelection();
          
          // Frissítjük az adatokat a hozzárendelés megjelenítéséhez
          this.refreshAssignmentData();
        }
      },
      error: (error) => {
        this.isAssigning = false;
        this.businessService.showError(`Hiba történt a hozzárendelés során: ${error.message || error}`);
      }
    });
  }

  removeSelectedAssignments() {
    const companyIds = this.selection.selected.map((row: CallCenterDatabaseTableData) => Number(row.id));
    
    this.callCenterAssignmentService.removeAssignments(companyIds).subscribe({
      next: (response) => {
        if (response.success) {
          // Clear selection after successful removal
          this.stateService.clearSelection();
          
          // Frissítjük az adatokat a törlés megjelenítéséhez
          this.refreshAssignmentData();
        }
      },
      error: (error) => {
        this.businessService.showError(`Hiba történt a hozzárendelések törlése során: ${error.message || error}`);
      }
    });
  }

  // New method to handle filter changes from the filters component
  onFiltersChanged(filterData: FilterData) {
    // Apply filter data to filter service
    this.stateService.applyFilterData(filterData);
    
    // Apply all filters
    this.applyAllFilters();
  }

  // Combined filter application method
  private applyAllFilters() {
    // Filter predicate is now built into stateService
    this.triggerFilterUpdate();
  }

  clearFilters() {
    this.stateService.clearFilters();
    this.dataSource.filter = '';
  }

  getStatusLabel(status: string): string {
    return CallCenterTableUtils.getStatusLabel(status);
  }

  // Method to normalize status for CSS class names (remove accents, convert to lowercase)
  getStatusClass(status: string): string {
    if (!status) return 'clickable-chip';
    return this.stateService.getCachedStatusClass(status, CallCenterTableUtils.getStatusClass);
  }

  editStatus(element: CallCenterDatabaseTableData, event: Event) {
    event.stopPropagation();
    
    // Ellenőrizzük, hogy már van-e nyitott/betöltődő dialog
    if (this.isDialogOpen || this.isLoadingDialog) {
      return;
    }

    // Loading állapot aktiválása
    this.isLoadingDialog = true;
    this.cdr.detectChanges();
    
    // Load company logs and open dialog
    this.businessService.api.getCompanyLogs(element.id).subscribe({
      next: (companyLogs) => {
        this.openStatusDialog(element, companyLogs);
      },
      error: (error) => {
        this.isLoadingDialog = false;
        this.cdr.detectChanges();
        const errorMessage = `Hiba történt a cég naplók betöltése során: ${error.message || error}`;
        this.businessService.showError(errorMessage);
        // Fallback to empty logs if backend call fails, but pass the error message
        this.openStatusDialog(element, [], errorMessage);
      }
    });
  }

  private openStatusDialog(element: CallCenterDatabaseTableData, companyLogs: any[], logsError?: string) {
    this.isDialogOpen = true;
    this.isLoadingDialog = false;
    this.cdr.detectChanges();
    
    // Use dialog service to open status dialog
    this.callCenterDialogService.openStatusDialog(element, companyLogs, this.statusOptions, logsError)
      .subscribe((result) => {
        this.isDialogOpen = false;
        this.cdr.detectChanges();
        
        if (result && result.confirmed && (result.saveChangesOnly || result.selectedStatus)) {
          this.businessService.processDialogResult(element, result, (companyId) => {
            this.reloadSingleElement(companyId);
          });
        }
      });
  }

  private updateDataSourceWithPagination() {
    this.stateService.updateDataSourceWithPagination(this.data, this.paginator);
  }

  // Chip menu actions
  copyToClipboard(text: string, event: Event) {
    event.stopPropagation();
    this.businessService.copyToClipboard(text);
  }

  addEmail(currentValue: string, event: Event) {
    event.stopPropagation();
    
    // Get the row element for finding company ID
    const target = event.target as HTMLElement;
    const row = target.closest('tr');
    const rowData = this.getRowDataFromElement(row);
    
    if (!rowData) {
      console.error('Nem sikerült azonosítani a sort');
      return;
    }

    // Use dialog service to open add email dialog
    this.callCenterDialogService.openAddEmailDialog().subscribe((result) => {
      if (result && result.confirmed && result.value) {
        this.businessService.api.addCompanyEmail(rowData.id, result.value).subscribe({
          next: (response) => {
            if (response.success) {
              // Update the row data emails array
              if (!rowData.emails || rowData.emails.length === 0) {
                rowData.emails = [result.value];
              } else {
                rowData.emails.push(result.value);
              }
              
              this.businessService.showSuccess('Email sikeresen hozzáadva');
              this.cdr.detectChanges();
            }
          },
          error: (error) => {
            this.businessService.showError(`Hiba történt az email hozzáadása során: ${error.message || error}`);
          }
        });
      }
    });
  }

  // Delegate methods to chip management service
  trackByIndex(index: number, item: any): any {
    return this.chipManagementService ? this.chipManagementService.trackByIndex(index, item) : index;
  }

  trackByElement(index: number, element: CallCenterDatabaseTableData): number {
    return this.chipManagementService ? this.chipManagementService.trackByElement(index, element) : element.id;
  }

  trackByEmailIndex(index: number, email: string): string {
    return this.chipManagementService ? this.chipManagementService.trackByEmailIndex(index, email) : email;
  }

  trackByChipConfig(index: number, chipConfig: CallCenterChipConfig): string {
    return this.chipManagementService ? this.chipManagementService.trackByChipConfig(index, chipConfig) : `${chipConfig.text}-${chipConfig.type}`;
  }

  getChipStatus(element: CallCenterDatabaseTableData, chipValue: string, chipType: 'official' | 'phone' | 'email'): 'default' | 'confirmed' | 'rejected' {
    return this.chipManagementService ? this.chipManagementService.getChipStatus(element, chipValue, chipType) : 'default';
  }

  parseOfficials(officials: string): string[] {
    return this.chipManagementService ? this.chipManagementService.parseOfficials(officials) : [];
  }

  parsePhoneNumbers(phoneNumbers: string): string[] {
    return this.chipManagementService ? this.chipManagementService.parsePhoneNumbers(phoneNumbers) : [];
  }

  // Helper method to get row data from DOM element
  getRowDataFromElement(rowElement: Element | null): CallCenterDatabaseTableData | null {
    if (!rowElement) return null;
    
    // Find row index
    const rows = Array.from(rowElement.parentElement?.children || []);
    const rowIndex = rows.indexOf(rowElement);
    
    if (rowIndex >= 0 && rowIndex < this.dataSource.filteredData.length) {
      return this.dataSource.filteredData[rowIndex];
    }
    
    return null;
  }


  /**
   * Frissíti a hozzárendelési adatokat
   * Szerver oldali lapozásnál a wrapper újratölti az aktuális oldalt
   */
  private refreshAssignmentData(): void {
    if (this.serverSidePagination) {
      // Szerver oldali: wrapper újratöltése
      this.dataChanged.emit();
      return;
    }
    
    // Kliens oldali kompatibilitás (legacy)
    const selectedIds = this.selection.selected.map(item => item.id);
    const currentPageIndex = this.paginator?.pageIndex || 0;
    const currentPageSize = this.paginator?.pageSize || 10;
    
    this.callCenterAssignmentService.refreshAssignmentData().subscribe({
      next: (updatedData) => {
        // Frissítjük az adatokat
        this.data = updatedData;
        this.dataSource.data = [...this.data];
        
        // Visszaállítjuk a kijelölést (ha még léteznek a rekordok)
        if (selectedIds.length > 0) {
          const newSelection = this.data.filter(item => selectedIds.includes(item.id));
          this.selection.clear();
          newSelection.forEach(item => this.selection.select(item));
        }
        
        // Visszaállítjuk a paginátor pozíciót
        if (this.paginator) {
          setTimeout(() => {
            if (this.paginator) {
              this.paginator.pageIndex = currentPageIndex;
              this.paginator.pageSize = currentPageSize;
            }
          });
        }
        
        // Trigger change detection
        this.cdr.detectChanges();
        
        // Értesítjük a parent komponenst
        this.dataChanged.emit();
      },
      error: (error) => {
        this.businessService.showError(`Hiba történt a hozzárendelési adatok frissítése során: ${error.message || error}`);
      }
    });
  }

  /**
   * Újratölti csak egy konkrét rekordot a backendből
   */
  private reloadSingleElement(companyId: number): void {
    this.businessService.api.reloadSingleElement(companyId).subscribe({
      next: (updatedItem) => {
        // Megkeressük az elemet a tömbben és frissítjük
        const index = this.data.findIndex(item => item.id === companyId);
        if (index !== -1) {
          this.data[index] = updatedItem;

          if (this.serverSidePagination) {
            // Szerver oldali lapozásnál csak az adatot frissítjük, paginator-t nem érintjük
            // hogy ne írja felül a totalRecords értéket
            this.stateService.updateDataOnly(this.data);
          } else {
            this.updateDataSourceWithPagination();
          }
          this.stateService.clearAllCaches();
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        this.businessService.showError(`Hiba történt a rekord újratöltése során: ${error.message || error}`);
      }
    });
  }

  // ✅ Checkbox functionality methods - delegate to state service
  isAllSelected() {
    return this.stateService.isAllSelected(this.paginator, this.hidePaginator);
  }

  toggleAllRows() {
    this.stateService.toggleAllRows(this.paginator, this.hidePaginator);
  }

  checkboxLabel(row?: CallCenterDatabaseTableData): string {
    return this.stateService.checkboxLabel(row, this.paginator, this.hidePaginator);
  }



  

  // ✅ Chip configuration methods using CallCenterChipComponent helpers - delegate to chip management service
  getStatusChipConfig(element: CallCenterDatabaseTableData): CallCenterChipConfig {
    return this.chipManagementService ? this.chipManagementService.getStatusChipConfig(element) : { text: element.status, type: 'status' };
  }

  getOfficialChipConfigs(element: CallCenterDatabaseTableData): CallCenterChipConfig[] {
    return this.chipManagementService ? this.chipManagementService.getOfficialChipConfigs(element) : [];
  }

  getPhoneChipConfigs(element: CallCenterDatabaseTableData): CallCenterChipConfig[] {
    return this.chipManagementService ? this.chipManagementService.getPhoneChipConfigs(element) : [];
  }

  getEmailChipConfigs(element: CallCenterDatabaseTableData): CallCenterChipConfig[] {
    return this.chipManagementService ? this.chipManagementService.getEmailChipConfigs(element) : [];
  }


}
